(() => {
  if (window.RSGiftSafety) return;
  const configNode = document.getElementById('rs-gift-safety-config');
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
  const gifts = config.gifts;
  if (!gifts?.cloth?.variantId || !gifts?.flask?.variantId) return;
  const strings = config.strings || {};
  const pollMs = config.pollMs || 300;
  const quietMs = config.quietMs || 800;
  const timeoutMs = config.timeoutMs || 15000;
  const requestTimeoutMs = config.requestTimeoutMs || 5000;
  const cartUrl = `${(config.cartUrl || '/cart').replace(/\.js$/, '').replace(/\/$/, '')}.js`;
  const pause = milliseconds => new Promise(resolve => window.setTimeout(resolve, milliseconds));
  const isMoney = amount => Number.isSafeInteger(amount) && amount >= 0;
  let revision = 0;
  let mutations = 0;
  let activityAt = Date.now();
  let running = null;
  let backgroundTimer = null;
  let replay = null;
  let approvedForm = null;
  let checkoutAttempt = null;
  const statuses = new Set();

  function hasCamperDiscount(cart) {
    const isCamper = code => String(code || '').trim().toUpperCase() === 'CAMPER';
    const codes = Array.isArray(cart.discount_codes)
      ? cart.discount_codes.filter(discount => isCamper(discount?.code)) : [];
    if (codes.length) return codes.some(discount => discount.applicable === true);
    return Array.isArray(cart.cart_level_discount_applications) &&
      cart.cart_level_discount_applications.some(discount =>
        discount?.type === 'discount_code' && isCamper(discount.title));
  }

  function inspectCart(cart, now = Date.now()) {
    const reasons = [];
    const quantities = { cloth: 0, flask: 0 };
    const expected = { cloth: null, flask: null };
    if (!cart || !Array.isArray(cart.items) || !isMoney(cart.total_price) || !cart.currency) {
      return { ready: false, reasons: ['invalid-cart'], quantities, expected };
    }
    let giftTotal = 0;
    let hasDualflow = false;
    for (const item of cart.items) {
      const quantity = item.quantity;
      if (!Number.isSafeInteger(quantity) || quantity < 1) reasons.push('invalid-quantity');
      if (String(item.product_id) === String(config.dualflowProductId) && quantity > 0) hasDualflow = true;
      for (const key of ['cloth', 'flask']) {
        if (String(item.variant_id || item.id) !== String(gifts[key].variantId)) continue;
        quantities[key] += quantity || 0;
        if (!isMoney(item.final_line_price)) reasons.push(`${key}-unknown-price`);
        else {
          giftTotal += item.final_line_price;
          if (item.final_line_price !== 0) reasons.push(`${key}-not-free`);
        }
      }
    }
    const subtotal = Math.max(0, cart.total_price - giftTotal);
    const active = now >= config.startsAt * 1000 && now < config.endsAt * 1000;
    const thresholdKnown = cart.currency === config.thresholdCurrency;
    if (active) {
      if (thresholdKnown) expected.cloth = subtotal < config.thresholdAmount ? 0 : gifts.cloth.available === false ? null : 1;
      expected.flask = !hasDualflow ? 0 : gifts.flask.available === false ? null : 1;
    }
    // CAMPER remains eligible independently of the timed USD 69 campaign.
    if (hasCamperDiscount(cart)) expected.cloth = gifts.cloth.available === false ? null : 1;
    for (const key of ['cloth', 'flask']) {
      if (quantities[key] > 1) reasons.push(`${key}-duplicate`);
      if (expected[key] !== null && quantities[key] !== expected[key]) reasons.push(`${key}-quantity`);
    }
    return { ready: reasons.length === 0, reasons, quantities, expected, subtotal, active, thresholdKnown, hasDualflow };
  }

  function signature(cart) {
    return JSON.stringify([cart.currency, cart.total_price, hasCamperDiscount(cart), cart.items.map(item => [item.key,
      item.variant_id || item.id, item.product_id, item.quantity, item.final_line_price,
      item.properties || {}, item.selling_plan_allocation?.selling_plan?.id || null])]);
  }

  function markDirty() {
    revision += 1;
    activityAt = Date.now();
    replay = null;
    approvedForm = null;
  }

  function error(code, inspection) {
    const result = new Error(strings.retry || code);
    result.name = 'RSGiftSafetyError';
    result.code = code;
    result.inspection = inspection;
    return result;
  }

  async function readCart(remaining) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), Math.min(requestTimeoutMs, remaining));
    try {
      const response = await fetch(cartUrl, { method: 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json' }, signal: controller.signal });
      if (!response.ok) throw error('CART_READ_FAILED');
      return await response.json();
    } finally { window.clearTimeout(timer); }
  }

  function ensure(options = {}) {
    if (running) return running;
    const deadline = Date.now() + (options.timeoutMs || timeoutMs);
    running = (async () => {
      let lastSignature = null;
      let stableReads = 0;
      let stableRevision = revision;
      let inspection = null;
      let lastError = null;
      while (Date.now() < deadline) {
        const readRevision = revision;
        if (stableRevision !== readRevision) {
          stableReads = 0;
          lastSignature = null;
          stableRevision = readRevision;
        }
        if (!mutations) {
          try {
            const cart = await readCart(deadline - Date.now());
            inspection = inspectCart(cart);
            if (revision !== readRevision || mutations) {
              stableReads = 0;
              lastSignature = null;
            } else {
              const nextSignature = signature(cart);
              stableReads = nextSignature === lastSignature ? stableReads + 1 : 1;
              lastSignature = nextSignature;
              if (inspection.ready && stableReads >= 2 && Date.now() - activityAt >= quietMs && Date.now() < deadline) {
                document.dispatchEvent(new CustomEvent('rs:gift-cart-ready', { detail: { cart, revision } }));
                return cart;
              }
            }
          } catch (caught) {
            lastError = caught;
            stableReads = 0;
            lastSignature = null;
          }
        } else {
          stableReads = 0;
          lastSignature = null;
        }
        const remaining = deadline - Date.now();
        if (remaining > 0) await pause(Math.min(pollMs, remaining));
      }
      const failure = error(lastError ? 'CART_READ_FAILED' : 'GIFT_SYNC_TIMEOUT', inspection);
      if (lastError) failure.cause = lastError;
      throw failure;
    })().finally(() => { running = null; });
    return running;
  }

  function schedule() {
    window.clearTimeout(backgroundTimer);
    backgroundTimer = window.setTimeout(() => { ensure().catch(() => {}); }, pollMs);
  }

  function isCheckoutLink(element) {
    if (!element?.matches('a[href]')) return false;
    try {
      const url = new URL(element.href, window.location.href);
      return url.origin === window.location.origin && /\/(?:checkout|checkouts)(?:\/|$)/.test(url.pathname);
    } catch (_) { return false; }
  }

  function checkoutControl(target) {
    const element = target instanceof Element ? target.closest('button, input[type="submit"], a[href], [data-rs-checkout]') : null;
    if (!element) return null;
    if (element.name === 'checkout' || element.hasAttribute('data-rs-checkout') || isCheckoutLink(element)) return element;
    const action = element.formAction || element.form?.action;
    if (action) {
      try { if (/\/(?:checkout|checkouts)(?:\/|$)/.test(new URL(action, location.href).pathname)) return element; } catch (_) {}
    }
    return null;
  }

  function descriptor(element, form) {
    return { element, id: element?.id, form: form || element?.form,
      formId: (form || element?.form)?.id, href: element?.getAttribute('href'), name: element?.name };
  }

  function formCheckout(form) {
    const named = form?.elements?.namedItem('checkout');
    if (named instanceof Element) return named;
    return Array.from(form?.elements || []).find(element => element.name === 'checkout') ||
      form?.querySelector('[data-rs-checkout]') || null;
  }

  function resolveControl(action) {
    if (action.element?.isConnected) return action.element;
    if (action.id) {
      const replacement = document.getElementById(action.id);
      if (replacement && checkoutControl(replacement)) return replacement;
    }
    const form = action.formId ? document.getElementById(action.formId) : action.form;
    return form?.isConnected ? formCheckout(form) : null;
  }

  function setStatus(action, state) {
    const control = resolveControl(action);
    let node = action.status;
    if (!node?.isConnected && control?.parentElement) {
      node = document.createElement('div');
      node.className = 'rs-gift-safety-status';
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
      node.setAttribute('aria-atomic', 'true');
      control.insertAdjacentElement('afterend', node);
      action.status = node;
      statuses.add(node);
    }
    if (!node) return;
    node.hidden = false;
    node.textContent = strings[state] || strings.retry || '';
    if (state === 'retry') {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = strings.retryButton || strings.retry || '';
      retry.addEventListener('click', () => startCheckout(action));
      node.appendChild(retry);
    }
  }

  function allowReplay(element, form, kind) {
    if (!replay || replay.revision !== revision || Date.now() > replay.expires) return false;
    if (kind === 'click' && replay.click && element === replay.element) {
      replay.click = false;
      return true;
    }
    if (kind === 'submit' && replay.submit && form === replay.form && (!element || element === replay.element)) {
      replay.submit = false;
      return true;
    }
    return false;
  }

  function startCheckout(action) {
    if (checkoutAttempt) return checkoutAttempt;
    setStatus(action, 'updating');
    action.element?.setAttribute('aria-busy', 'true');
    checkoutAttempt = ensure().then(async cart => {
      const confirmedRevision = revision;
      if (window.RSNativeCartSync?.refresh) {
        const refreshed = await window.RSNativeCartSync.refresh();
        if (refreshed !== true) throw error('CART_DISPLAY_NOT_READY');
        const latestCart = await readCart(requestTimeoutMs);
        if (revision !== confirmedRevision || !inspectCart(latestCart).ready || signature(cart) !== signature(latestCart)) {
          throw error('CART_CHANGED_BEFORE_CHECKOUT');
        }
      } else if (resolveControl(action)?.form?.querySelector('[name="updates[]"]')) {
        throw error('CART_DISPLAY_NOT_READY');
      }
      const control = resolveControl(action);
      if (!control || control.disabled || mutations) throw error('CHECKOUT_CHANGED');
      for (const status of statuses) status.hidden = true;
      control.removeAttribute('aria-busy');
      replay = { element: control, form: control.form || action.form, revision,
        click: true, submit: true, expires: Date.now() + 1000 };
      approvedForm = { form: replay.form, revision, expires: Date.now() + 10000 };
      control.click();
      const allowed = replay;
      window.setTimeout(() => { if (replay === allowed) replay = null; }, 1000);
    }).catch(() => { approvedForm = null; setStatus(action, 'retry'); }).finally(() => {
      action.element?.removeAttribute('aria-busy');
      checkoutAttempt = null;
    });
    return checkoutAttempt;
  }

  document.addEventListener('click', event => {
    const control = checkoutControl(event.target);
    if (!control || allowReplay(control, control.form, 'click')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    startCheckout(descriptor(control));
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const control = event.submitter || formCheckout(form);
    const checkout = checkoutControl(control);
    if (!checkout || (event.submitter && !checkoutControl(event.submitter))) return;
    if (allowReplay(control, form, 'submit')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    startCheckout(descriptor(control, form));
  }, true);

  document.addEventListener('formdata', event => {
    if (!approvedForm || event.target !== approvedForm.form || approvedForm.revision !== revision ||
      Date.now() > approvedForm.expires || !event.formData?.has('checkout')) return;
    event.formData.delete('updates[]');
  }, true);

  document.addEventListener('rs:cart-mutating', () => { mutations += 1; markDirty(); });
  document.addEventListener('rs:cart-mutated', () => { mutations = Math.max(0, mutations - 1); markDirty(); schedule(); });
  for (const eventName of ['product-ajax:added', 'cart:refresh']) {
    document.addEventListener(eventName, event => {
      if (event.detail?.rsDisplayOnly) return;
      markDirty();
      schedule();
    });
  }
  window.addEventListener('pageshow', event => { if (event.persisted) mutations = 0; markDirty(); schedule(); });
  window.RSGiftSafety = { ensure, markDirty, inspectCart };
  schedule();
})();
